/**
 * The timezones a user can pick, in one place.
 *
 * WHY THIS EXISTS. The list lived inside GeneralSettings as a computed property, and the scheduled-task
 * editor needed the same one. Two copies of a list whose values are PERSISTED and then resolved
 * server-side would not stay equal for long, and the failure would be quiet: a zone offered on one
 * screen and missing from the other, or the same city spelled with two different ids.
 *
 * The values matter more than they look. They are stored on a scheduled task's trigger and resolved by
 * TimeZoneInfo.FindSystemTimeZoneById, which throws on an id it does not recognise — and that call is
 * unguarded, so an unknown value surfaces as a 500 rather than as a readable error. Editing this list is
 * editing what the scheduler can be asked to do.
 *
 * Labels are i18n KEYS, not text. This module has no Vue instance, so it cannot call $t; exporting keys
 * keeps it testable without i18n and leaves rendering to whoever imports it. Callers render
 * `option.labelKey ? $t(option.labelKey) : option.label`.
 *
 * Adding a zone means this array plus the matching keys in en/es/fr (i18n/index.spec.js enforces that
 * the three catalogues carry exactly the same keys), and nothing else.
 *
 * This list was US-only until the European zones below were added, inherited from the settings screen it
 * came from. That was recorded here as "a real gap on the European branch — a store in France has nothing
 * here to pick", and the gap had a cost: with nothing selectable and an American default underneath, a
 * task asked for at 07:00 in Paris was stamped New York and fired at 13:00 on its owner's clock.
 */
export const TIMEZONE_OPTIONS = [
  { labelKey: 'settings.general.timezones.newYork', value: 'America/New_York' },
  { labelKey: 'settings.general.timezones.chicago', value: 'America/Chicago' },
  { labelKey: 'settings.general.timezones.denver', value: 'America/Denver' },
  { labelKey: 'settings.general.timezones.phoenix', value: 'America/Phoenix' },
  { labelKey: 'settings.general.timezones.losAngeles', value: 'America/Los_Angeles' },
  { labelKey: 'settings.general.timezones.anchorage', value: 'America/Anchorage' },
  { labelKey: 'settings.general.timezones.honolulu', value: 'Pacific/Honolulu' },
  { labelKey: 'settings.general.timezones.london', value: 'Europe/London' },
  { labelKey: 'settings.general.timezones.dublin', value: 'Europe/Dublin' },
  { labelKey: 'settings.general.timezones.lisbon', value: 'Europe/Lisbon' },
  { labelKey: 'settings.general.timezones.paris', value: 'Europe/Paris' },
  { labelKey: 'settings.general.timezones.brussels', value: 'Europe/Brussels' },
  { labelKey: 'settings.general.timezones.amsterdam', value: 'Europe/Amsterdam' },
  { labelKey: 'settings.general.timezones.madrid', value: 'Europe/Madrid' },
  { labelKey: 'settings.general.timezones.berlin', value: 'Europe/Berlin' },
  { labelKey: 'settings.general.timezones.rome', value: 'Europe/Rome' },
  { labelKey: 'settings.general.timezones.zurich', value: 'Europe/Zurich' },
  { labelKey: 'settings.general.timezones.vienna', value: 'Europe/Vienna' },
  { labelKey: 'settings.general.timezones.stockholm', value: 'Europe/Stockholm' },
  { labelKey: 'settings.general.timezones.warsaw', value: 'Europe/Warsaw' },
  { labelKey: 'settings.general.timezones.athens', value: 'Europe/Athens' },
];

/**
 * The zone this browser is actually in, or '' when the platform will not say.
 *
 * This is what makes a NEW task default to the user's own clock instead of an American one. It is
 * deliberately the same call ScheduleSuggestionChip already makes — that path got this right first, and
 * two ways of asking "where is the user" would not stay equal.
 *
 * Guarded because Intl.DateTimeFormat().resolvedOptions() is not universally safe to call: it throws on
 * some locked-down configurations, and a throw here would take down a form whose only job was a rename.
 * An empty string means "no answer", which every caller treats as "fall through to the next source" —
 * never as a zone.
 */
export function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

/**
 * The options to offer when a value is already stored, keeping that value selectable even when it is not
 * one of ours.
 *
 * The field this replaces was free text, so stored values are not limited to the list above — a European
 * store, or a Windows id like "Eastern Standard Time" that the server also accepts. A picker that dropped
 * the current value would rewrite a task's schedule as a side effect of opening its form, which is a read
 * turning into a write nobody asked for.
 *
 * Returns a new array; the canonical list is never mutated.
 */
export function timezoneOptionsFor(current) {
  if (!current || TIMEZONE_OPTIONS.some(o => o.value === current)) return [...TIMEZONE_OPTIONS];
  return [...TIMEZONE_OPTIONS, { label: current, value: current }];
}

/**
 * The same options in the {value, label} shape FusionDropdown reads, each labelKey rendered through `t`.
 *
 * `t` is the caller's $t, passed in because this module has no Vue instance (see the header). One place
 * for the `labelKey ? $t(labelKey) : label` rule, which every scheduled-task picker used to spell out.
 */
export function timezoneDropdownOptions(options, t) {
  return options.map(z => ({ value: z.value, label: z.labelKey ? t(z.labelKey) : z.label }));
}
